import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Badge,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Avatar,
  Drawer,
  Textarea,
  Modal,
  Divider,
  ActionIcon,
  Tooltip,
  Skeleton,
  Box,
} from '@mantine/core';
import {
  IconTable,
  IconLayoutGrid,
  IconSearch,
  IconEye,
  IconAdjustments,
  IconPlus,
  IconUserMinus,
  IconUserPlus,
  IconTrash,
  IconRefresh,
} from '@tabler/icons-react';
import { Flex, Select, TextInput, CCMSDataTable, StreamProgress, ListPagination } from '@/components';
import { ContactCard, type Contact } from '../Components/ContactCard';
import { EmptyState } from '../Components/EmptyState';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useFacilitatorService,
  FacilitatorModelView,
  CreateFacilitator,
  useUserService,
  UserModelView,
  useFacilitatorStatusService,
  FacilitatorStatus,
  useFacilitatorStreaming,
} from '@actschurch/ccms-sdk';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import { useAssignedFacilitators, subtractListed, splitName } from '../unlistedAssignees';
import { sortRows } from '@/utils/tableSort';

/**
 * A facilitator row, either streamed from `facilitatorService.list()` or synthesised for someone who is
 * referenced by an assignment but has no facilitator record. The `unlisted` marker separates the two.
 */
type FacilitatorRow = FacilitatorModelView & {
  /** Present only on synthesised rows. Carries how many assignments reference the person. */
  unlisted?: { assignmentCount: number };
};

export default function FacilitatorPage() {
  const navigate = useNavigate();
  const { refetch } = useSidebarCountsContext();

  // ─── SDK services ──────────────────────────────────────────────────────────
  const Facilitators        = useFacilitatorService();
  const Users               = useUserService();
  const FacilitatorStatuses = useFacilitatorStatusService();

  // ─── View / pagination / sort ──────────────────────────────────────────────
  const [view, setView]             = useState<'table' | 'cards'>('cards');
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize]     = useState(12);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  /** Busy flag for mutations (create / remove). List loading comes from the stream below. */
  const [loading, setLoading]       = useState(false);

  // ─── Data lists ────────────────────────────────────────────────────────────
  const [users, setUsers]               = useState<UserModelView[]>([]);
  const [statuses, setStatuses]         = useState<FacilitatorStatus[]>([]);

  // ─── Add Facilitator Drawer ────────────────────────────────────────────────
  const [addOpen, setAddOpen]                     = useState(false);
  const [memberSearch, setMemberSearch]           = useState('');
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // stagedUserIds — users queued to become facilitators when "Done" is clicked
  const [stagedUserIds, setStagedUserIds] = useState<Set<string>>(new Set());

  // addForm — default bio and statusID applied to every facilitator in this batch
  const [addForm, setAddForm] = useState<{ bio: string; statusID: number }>({
    bio: '',
    statusID: 0,
  });

  // ─── Delete confirmation modal ─────────────────────────────────────────────
  // Still used by the table view's Remove action — the card view no longer
  // triggers this at all, since ContactCard has no delete affordance.
  const [deleteTarget, setDeleteTarget] = useState<FacilitatorModelView | null>(null);
  const [deleteOpen, setDeleteOpen]     = useState(false);

  // ─── Search / filters ──────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]                         = useState('');
  const [filtersOpen, setFiltersOpen]                       = useState(false);
  const [selectedStatus, setSelectedStatus]                 = useState<string | null>(null);

  const isFilterActive = Boolean(selectedStatus || searchTerm);

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedStatus(null);
  };

  // ─── Facilitator display helpers ───────────────────────────────────────────

  // Resolve the best available display name from the model view
  const getFacilitatorFullName = (f: FacilitatorRow) => {
    const fromParts = [f.firstName, f.surname].filter(Boolean).join(' ').trim();
    if (fromParts) return fromParts;
    const full = f.fullName?.trim();
    if (full) return full;
    // Reject displayName if it looks like a raw userId GUID
    const display = f.displayName?.trim();
    if (display && display !== f.userId) return display;
    return 'Unknown';
  };

  const getFacilitatorAvatar = (f: FacilitatorRow) => f.avatar ?? undefined;
  const getFacilitatorActive = (f: FacilitatorRow) => f.isActive ?? false;

  // Maps a FacilitatorModelView into the generic Contact shape ContactCard
  // expects. Facilitators don't have a separate "type" concept like leaders
  // do, so status doubles up as both the typeName/typeColor pill.
  const facilitatorToContact = (f: FacilitatorRow): Contact => {
    const [firstName, ...rest] = getFacilitatorFullName(f).split(' ');
    return {
      id: f.userId || String(f.memberID),
      firstName: f.firstName || firstName || '',
      lastName: f.surname || rest.join(' ') || '',
      avatar: getFacilitatorAvatar(f) ?? null,
      typeName: f.statusName ?? undefined,
      typeColor: f.statusColor ?? undefined,
      activeAssignmentsCount: f.activeAssignmentsCount ?? 0,
      locationsCount: f.locationsCount ?? 0,
      coursesCount: f.coursesCount ?? 0,
      isActive: getFacilitatorActive(f),
      unlistedNote: f.unlisted ? 'No user account' : undefined,
    };
  };

  // ─── Data fetching ─────────────────────────────────────────────────────────

  // Facilitators arrive in 50-row batches via the SDK stream, so cards/rows paint progressively.
  // Search and the status/location filters stay client-side — the full set is in memory, so
  // filtering locally gives the same result without re-streaming.
  // `fetchFacilitators` is the stream's `load`, so existing post-mutation refresh calls still work.
  const {
    items: facilitators,
    isLoading: streaming,
    progress,
    loadedCount,
    load: fetchFacilitators,
  } = useFacilitatorStreaming(undefined, 50);

  /** True while the list is being (re)filled, whether by the stream or a mutation refresh. */
  const listLoading = streaming || loading;

  // Kept in a ref so the sidebar-count refresh doesn't participate in the stream effect's deps.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Fetch facilitator status lookup data for the drawer dropdown
  const fetchStatuses = useCallback(async () => {
    try {
      setStatuses(await FacilitatorStatuses.getAll());
    } catch (e) {
      console.error(e);
    }
  }, [FacilitatorStatuses]);

  useEffect(() => {
    fetchFacilitators().then(() => refetchRef.current());
  }, [fetchFacilitators]);
  useEffect(() => { fetchStatuses(); },    [fetchStatuses]);

  // Backend user search — fires on every memberSearch keystroke via useEffect
  const fetchUsers = useCallback(async () => {
    if (!memberSearch.trim()) { setUsers([]); return; }
    setUserSearchLoading(true);
    try {
      setUsers(await Users.search({ searchTerm: memberSearch }));
    } catch (e) {
      console.error(e);
    } finally {
      setUserSearchLoading(false);
    }
  }, [Users, memberSearch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ─── Derived user lists ────────────────────────────────────────────────────

  // Exclude users that are already facilitators or already staged in this session
  const alreadyFacilitatorUserIds = new Set(facilitators.map((f) => f.userId).filter(Boolean));
  const searchedUsers = users.filter((u) => {
    if (!u.id) return false;
    if (alreadyFacilitatorUserIds.has(u.id) || stagedUserIds.has(u.id)) return false;
    return true;
  });

  // Full UserModelView objects for the "To Be Added" queue header
  const stagedUsersList = users.filter((u) => u.id && stagedUserIds.has(u.id));

  // ─── Stage / unstage users ─────────────────────────────────────────────────
  const toggleStageUser = (id: string) => {
    setStagedUserIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ─── Reset drawer to initial state ────────────────────────────────────────
  // Called on close so the next open always starts completely fresh
  const resetDrawer = () => {
    setMemberSearch('');
    setStagedUserIds(new Set());
    setUsers([]);
    setAddForm({ bio: '', statusID: 0 });
  };

  // ─── Submit staged facilitators ────────────────────────────────────────────
  // Creates one facilitator per staged user, each with the same bio + statusID
  const handleDoneAdding = async () => {
    if (stagedUserIds.size === 0) { setAddOpen(false); resetDrawer(); return; }
    setLoading(true);
    try {
      await Promise.all(
        Array.from(stagedUserIds).map((userId) => {
          const payload: CreateFacilitator = {
            userId,
            memberID: 0,   // resolved server-side from userId
            bio: addForm.bio,
            statusID: addForm.statusID,
          };
          return Facilitators.create(payload);
        })
      );
      await fetchFacilitators();
      toast.success('Facilitators Added', `Successfully added ${stagedUserIds.size} new facilitator(s).`);
      setAddOpen(false);
      resetDrawer();
    } catch (e) {
      console.error(e);
      toast.error('Failed', 'Could not add facilitators. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Remove Facilitator (table view only) ──────────────────────────────────
  const confirmDelete = (f: FacilitatorModelView) => {
    setDeleteTarget(f);
    setDeleteOpen(true);
  };

  const handleRemoveFacilitator = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await Facilitators.delete(deleteTarget.userId);
      await fetchFacilitators();
      setDeleteOpen(false);
      setDeleteTarget(null);
      toast.warning(
        'Facilitator Removed',
        `${getFacilitatorFullName(deleteTarget)} has been removed as a facilitator.`
      );
    } catch (e) {
      console.error(e);
      toast.error('Failed', 'Could not remove facilitator. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Facilitators referenced by work but with no facilitator record ────────
  // These never come back from `facilitatorService.list()`, so they are rebuilt from the assignment
  // sources and merged in below. See `../unlistedAssignees.ts` for why the registration list cannot be
  // used directly: it carries a lead facilitator name but no userId.
  const { people: assignedFacilitators, isLoading: assignedLoading } = useAssignedFacilitators();

  const unlistedRows = useMemo<FacilitatorRow[]>(() => {
    const listed = new Set(facilitators.map((f) => f.userId).filter(Boolean));
    return subtractListed(assignedFacilitators, listed).map((person) => {
      const { firstName, lastName } = splitName(person.name);
      return {
        userId: person.userId,
        memberID: 0,
        firstName,
        surname: lastName,
        avatar: person.avatar,
        statusID: 0,
        statusName: person.typeName,
        statusColor: person.typeColor,
        activeAssignmentsCount: person.assignmentCount,
        coursesCount: 0,
        locationsCount: 0,
        isActive: false,
        unlisted: { assignmentCount: person.assignmentCount },
      };
    });
  }, [assignedFacilitators, facilitators]);

  const allFacilitators = useMemo<FacilitatorRow[]>(
    () => [...facilitators, ...unlistedRows],
    [facilitators, unlistedRows],
  );

  /** How many rows on this page exist only because an assignment points at them. */
  const unlistedCount = unlistedRows.length;

  /**
   * Opens the detail page. For an unlisted person the cached name rides along in router state, because
   * the detail page cannot look it up: there is no facilitator record to fetch.
   */
  const goToFacilitator = (f: FacilitatorRow) =>
    navigate(
      `/Information/Facilitators/${f.userId || f.memberID}`,
      f.unlisted ? { state: { unlistedName: getFacilitatorFullName(f) } } : undefined,
    );

  // ─── Frontend filtering ────────────────────────────────────────────────────
  const filteredFacilitators = allFacilitators.filter((f) => {
    const fullName = getFacilitatorFullName(f);

    const matchesSearch =
      fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (f.statusName ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (f.bio ?? '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = selectedStatus ? f.statusName === selectedStatus : true;

    return matchesSearch && matchesStatus;
  });

  // ─── Frontend sorting ──────────────────────────────────────────────────────
  const columns: TableColumn<FacilitatorRow>[] = [
    {
      key: 'avatar',
      header: 'Avatar',
      accessor: (f) => f,
      sortable: false,
      width: 80,
      align: 'center',
      render: (_, f) => {
        const name   = getFacilitatorFullName(f);
        const avatar = getFacilitatorAvatar(f);
        return (
          <Avatar src={avatar} alt={name} size="md">
            {!avatar && name.charAt(0).toUpperCase()}
          </Avatar>
        );
      },
    },
    {
      key: 'name',
      header: 'Name',
      accessor: (f) => getFacilitatorFullName(f),
      sortable: true,
      render: (value, f) => (
        <Stack gap={2}>
          <Group gap="xs" align="center">
            <Text fw={600}>{value}</Text>
            {f.unlisted ? (
              <Badge color="orange" variant="light" size="xs">
                No user account
              </Badge>
            ) : (
              <Badge color={getFacilitatorActive(f) ? 'green' : 'red'} variant="light" size="xs">
                {getFacilitatorActive(f) ? 'Active' : 'Inactive'}
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            {f.unlisted
              ? `Assigned to ${f.unlisted.assignmentCount} item${f.unlisted.assignmentCount === 1 ? '' : 's'}, not on the facilitator list`
              : f.email ?? 'No email'}
          </Text>
        </Stack>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (f) => f.statusName ?? 'N/A',
      sortable: true,
      // Colorize the badge using the statusColor hex from the model view
      render: (value, f) => (
        <Badge
          variant="light"
          style={{
            backgroundColor: f.statusColor ? `${f.statusColor}22` : undefined,
            color: f.statusColor ?? undefined,
          }}
        >
          {value}
        </Badge>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      accessor: (f) => f.phone ?? 'N/A',
      sortable: true,
    },
    {
      key: 'activeAssignments',
      header: 'Active Assignments',
      accessor: (f) => f.activeAssignmentsCount ?? 0,
      sortable: true,
      align: 'center',
    },
    {
      key: 'locationsCount',
      header: 'Locations',
      accessor: (f) => f.locationsCount ?? 0,
    },
    {
      key: 'coursesCount',
      header: 'Courses',
      accessor: (f) => f.coursesCount ?? 0,
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (f) => f,
      sortable: false,
      align: 'center',
      render: (_, f) => (
        <Group gap={6} justify="center">
          <Button
            variant="light"
            color="blue"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              goToFacilitator(f);
            }}
          >
            <IconEye size={16} />
          </Button>
        </Group>
      ),
    },
  ];

  /**
   * Sorted by the clicked column's own `accessor`, so the key can never disagree with the cell.
   * ⚠️ Replaces a `switch` kept in parallel with the columns array. It had cases for `courseCount`,
   * `locationCount` and `active` that no column used, and **no** case for `phone`, which was marked
   * sortable, so clicking Phone sorted every row by `''`.
   */
  const sortedFacilitators = useMemo(
    () => sortRows(filteredFacilitators, columns, sortConfig),
    [filteredFacilitators, columns, sortConfig],
  );

  // ─── Pagination ────────────────────────────────────────────────────────────
  const totalItems = sortedFacilitators.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Reset to page 1 whenever filters or page size change
  useEffect(() => { setActivePage(1); }, [
    searchTerm, selectedStatus, pageSize,
  ]);

  // Clamp activePage if the total number of pages shrinks
  useEffect(() => {
    if (activePage > totalPages) setActivePage(totalPages);
  }, [activePage, totalPages]);

  const paginatedFacilitators = useMemo(() => {
    const firstIndex = (activePage - 1) * pageSize;
    return sortedFacilitators.slice(firstIndex, firstIndex + pageSize);
  }, [sortedFacilitators, activePage, pageSize]);

  // ─── Empty-state helpers ───────────────────────────────────────────────────
  // Includes the assignment lookup, so the page does not flash "no facilitators" before it resolves.
  const anyLoading = listLoading || assignedLoading;
  const hasNoFacilitatorsAtAll = !anyLoading && allFacilitators.length === 0;
  const hasNoFilteredResults = !anyLoading && allFacilitators.length > 0 && sortedFacilitators.length === 0;

  // ─── Table columns ─────────────────────────────────────────────────────────

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">
      <Title order={2}>Facilitators</Title>
      <Text mb={unlistedCount > 0 ? 'xs' : 'lg'}>
        Manage who serves as a course facilitator across the church.
      </Text>
      {unlistedCount > 0 && (
        <Text size="sm" c="orange" mb="lg">
          {unlistedCount} {unlistedCount === 1 ? 'person is' : 'people are'} assigned to work but{' '}
          {unlistedCount === 1 ? 'has' : 'have'} no user account, so{' '}
          {unlistedCount === 1 ? 'it is' : 'they are'} not on the facilitator list. Open{' '}
          {unlistedCount === 1 ? 'the entry' : 'an entry'} to create the account.
        </Text>
      )}

      {/* ── Search and Filter bar ── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Flex gap="sm" align="center" justify="space-between" wrap="wrap">
          <Flex gap="sm" align="center">
            <TextInput
              placeholder="Search by name or status"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
              styles={{ input: { width: '300px' } }}
              size="sm"
            />
            <Button
              variant="outline"
              leftSection={<IconAdjustments size={16} />}
              onClick={() => setFiltersOpen(true)}
            >
              Filters
            </Button>
            {isFilterActive && (
              <Button variant="subtle" onClick={clearAllFilters}>
                Clear Filters
              </Button>
            )}
          </Flex>
          <Group gap="sm">
            <Tooltip label="Refresh facilitator list">
            <ActionIcon variant="subtle" color="blue" onClick={() => fetchFacilitators()} loading={listLoading} title="Refresh">
              <IconRefresh size={16} />
            </ActionIcon>
            </Tooltip>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpen(true)}>
            Add Facilitator
          </Button>
          </Group>
        </Flex>
      </Card>

      {/* ════════════════════════════════════════════════════════════════════
          Add / Manage Facilitators Drawer
          ════════════════════════════════════════════════════════════════════ */}
      <Drawer
        opened={addOpen}
        onClose={() => { setAddOpen(false); resetDrawer(); }}
        title={
          <Group gap="xs">
            <IconUserPlus size={20} />
            <Text fw={600} size="md">Manage Facilitators</Text>
          </Group>
        }
        position="right"
        size={500}
        padding="md"
      >
        {/* Outer scroll area keeps entire drawer navigable without page scroll */}
          <Stack gap="xs" pb="xl">

            {/* ── To Be Added queue ──────────────────────────────────────────
                One dismissible card per staged user.
                Hidden entirely when the queue is empty.
            ─────────────────────────────────────────────────────────────────── */}
            {stagedUserIds.size > 0 && (
              <Stack gap={6} mb="xs">
                <Text size="sm" fw={600}>To Be Added ({stagedUserIds.size})</Text>
                {stagedUsersList.map((user) => {
                  const fullName =
                    user.fullName?.trim() ||
                    [user.firstName, user.surname].filter(Boolean).join(' ') ||
                    user.email ||
                    'Unknown';
                  return (
                    <Card
                      key={user.id}
                      withBorder
                      radius="md"
                      padding="sm"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <Group gap="md">
                        <Avatar src={user.avatar} alt={fullName} size="md" radius="xl">
                          {!user.avatar && fullName.charAt(0).toUpperCase()}
                        </Avatar>
                        <div>
                          <Text size="sm" fw={600}>{fullName}</Text>
                          <Text size="sm" c="dimmed">{user.email}</Text>
                        </div>
                      </Group>
                      {/* Remove from staged queue */}
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="lg"
                        radius="md"
                        onClick={() => user.id && toggleStageUser(user.id)}
                      >
                        <IconUserMinus size={18} />
                      </ActionIcon>
                    </Card>
                  );
                })}
              </Stack>
            )}

            <Divider label="Add People" labelPosition="center" my="sm" />

            {/* ── Member search ───────────────────────────────────────────────
                Every keystroke updates memberSearch → fetchUsers fires via
                useEffect → filtered results appear in searchedUsers below.
            ─────────────────────────────────────────────────────────────────── */}
            <TextInput
              placeholder="Search by name or email"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
            />

            {/* Result count / loading state hint */}
            {memberSearch.trim().length > 0 && !userSearchLoading && (
              <Text size="xs" c="dimmed">
                {searchedUsers.length} result{searchedUsers.length !== 1 ? 's' : ''}
              </Text>
            )}
            {userSearchLoading && <Text size="xs" c="dimmed">Searching...</Text>}

            {/* ── Search result cards ────────────────────────────────────────
                Already-staged and already-facilitator users are excluded.
                Each card shows a stage button (green +) or an "Added" badge.
                Shows an empty hint when a search returns nothing.
            ─────────────────────────────────────────────────────────────────── */}
            {memberSearch.trim().length > 0 && !userSearchLoading && searchedUsers.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No matching members found.
              </Text>
            ) : (
              <Stack gap={6}>
                {searchedUsers.map((user) => {
                  const fullName =
                    user.fullName?.trim() ||
                    [user.firstName, user.surname].filter(Boolean).join(' ') ||
                    user.email ||
                    'Unknown';
                  const isStaged = user.id ? stagedUserIds.has(user.id) : false;
                  return (
                    <Card
                      key={user.id}
                      withBorder
                      radius="md"
                      padding="sm"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <Group gap="md">
                        <Avatar src={user.avatar} alt={fullName} size="md" radius="xl">
                          {!user.avatar && fullName.charAt(0).toUpperCase()}
                        </Avatar>
                        <div>
                          <Text size="sm" fw={600}>{fullName}</Text>
                          <Text size="sm" c="dimmed">{user.email}</Text>
                        </div>
                      </Group>
                      {isStaged ? (
                        <Badge size="sm" color="green" variant="light">Added</Badge>
                      ) : (
                        <ActionIcon
                          variant="subtle"
                          color="green"
                          size="lg"
                          radius="md"
                          onClick={() => user.id && toggleStageUser(user.id)}
                        >
                          <IconUserPlus size={18} />
                        </ActionIcon>
                      )}
                    </Card>
                  );
                })}
              </Stack>
            )}

            {/* ════════════════════════════════════════════════════════════════
                Default Settings
                Applied uniformly to every facilitator created in this batch.
                ════════════════════════════════════════════════════════════════ */}
            <Divider label="Default Settings" labelPosition="center" my="sm" />

            {/* Status single-select — populated from FacilitatorStatus lookup.
                The statusID is what gets persisted on the facilitator record. */}
            <Select
              label="Facilitator Status"
              placeholder="Select a status"
              data={statuses.map((s) => ({
                value: String(s.statusID),
                label: s.name ?? `Status ${s.statusID}`,
              }))}
              value={addForm.statusID ? String(addForm.statusID) : null}
              onChange={(value) => setAddForm({ ...addForm, statusID: Number(value) || 0 })}
            />

            {/* Biography free-text — optional, applied to all staged users */}
            <Textarea
              label="Default Biography"
              placeholder="Facilitator biography (optional)"
              value={addForm.bio ?? ''}
              onChange={(e) => setAddForm({ ...addForm, bio: e.currentTarget.value })}
              minRows={3}
            />

          </Stack>

        {/* ── Done button ────────────────────────────────────────────────────
            Disabled until at least one user is staged. Spinner while the
            batch Facilitators.create() calls are in flight.
        ─────────────────────────────────────────────────────────────────────── */}
        <Group justify="flex-end" mt="lg">
          <Button
            loading={loading}
            onClick={handleDoneAdding}
            disabled={stagedUserIds.size === 0}
          >
            Done
          </Button>
        </Group>
      </Drawer>

      {/* ── Delete Confirmation Modal (table view only) ── */}
      <Modal
        opened={deleteOpen}
        onClose={() => { setDeleteOpen(false); setDeleteTarget(null); }}
        title={
          <Group gap="xs">
            <IconTrash size={18} color="red" />
            <Text fw={600}>Remove Facilitator</Text>
          </Group>
        }
        centered
        size="sm"
      >
        <Text size="sm" mb="lg">
          Are you sure you want to remove{' '}
          <strong>{deleteTarget ? getFacilitatorFullName(deleteTarget) : ''}</strong> as a
          facilitator? This action cannot be undone.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={() => { setDeleteOpen(false); setDeleteTarget(null); }}>
            Cancel
          </Button>
          <Button color="red" loading={loading} onClick={handleRemoveFacilitator}>
            Remove Facilitator
          </Button>
        </Group>
      </Modal>

      {/* ── Filter Drawer ── */}
      <Drawer
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filters"
        position="right"
        size="md"
        padding="md"
      >
        <Stack>
          {/* Filter by facilitator full name */}
          <TextInput
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              radius="md"
            /> 
          {/* Filter by status name — unique values derived from loaded facilitators */}
          <Select
            label="Status"
            placeholder="Select a status"
            data={Array.from(new Set(facilitators.map((f) => f.statusName || '')))
              .filter(Boolean)
              .map((s) => ({ value: s, label: s }))}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
          <Group align="right" mt="sm">
            <Button
              variant="light"
              onClick={() => {
                clearAllFilters();
                setFiltersOpen(false);
              }}
            >
              Clear
            </Button>
            <Button onClick={() => setFiltersOpen(false)}>Apply</Button>
          </Group>
        </Stack>
      </Drawer>

      {/* ── Streaming progress ── */}
      <StreamProgress
        loading={streaming}
        progress={progress}
        loadedCount={loadedCount}
        label="facilitators"
      />

      {/* ── View toggle ── */}
      <Flex gap="sm" justify="flex-end" mb="md">
        <Button 
          variant={view === 'cards' ? 'filled' : 'light'} 
          color="blue" 
          onClick={() => setView('cards')}
          aria-label="Cards view"
        >
          <IconLayoutGrid size={16} />
        </Button>
        
        <Button 
          variant={view === 'table' ? 'filled' : 'light'} 
          color="blue" 
          onClick={() => setView('table')}
          aria-label="Table view"
        >
          <IconTable size={16} />
        </Button>
      </Flex>

      {/* ── Empty states (shared across both views) ── */}
      {hasNoFacilitatorsAtAll ? (
        <Card withBorder radius="md" padding="lg">
          <EmptyState
            variant="no-data"
            entityLabel="facilitators"
            addLabel="Add Facilitator"
            onAdd={() => setAddOpen(true)}
          />
        </Card>
      ) : hasNoFilteredResults ? (
        <Card withBorder radius="md" padding="lg">
          <EmptyState
            variant="no-results"
            entityLabel="facilitators"
            onClearFilters={clearAllFilters}
          />
        </Card>
      ) : view === 'table' ? (
        /* ── Table view ── */
        <Card withBorder radius="md" padding="lg">
          <CCMSDataTable
            data={paginatedFacilitators}
            columns={columns}
            loading={listLoading && facilitators.length === 0}
            sortable
            onSort={(config) => {
              setSortConfig(config);
              setActivePage(1);
            }}
            onRowClick={(f) =>
              goToFacilitator(f)
            }
            striped
            highlightOnHover
          />
          <ListPagination
            page={activePage}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={(page) => setActivePage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setActivePage(1);
            }}
            showPageSize
          />
        </Card>
       ) : (
          /* ── Card view ── */
          <Card withBorder radius="md" padding="lg">
            <SimpleGrid cols={4} spacing="lg" mb="md">
              {listLoading && facilitators.length === 0
                ? Array.from({ length: pageSize }).map((_, index) => (
                    <Box key={`facilitator-skeleton-${index}`}>
                      <Skeleton height={240} radius="md" />
                    </Box>
                  ))
                : paginatedFacilitators.map((f) => {
                    const contact = facilitatorToContact(f);
                    const facilitatorId = contact.id;

                    return (
                      <ContactCard
                        key={facilitatorId}
                        contact={contact}
                        showActiveAssignments
                        showLocationsCount
                        showCoursesCount
                        hideStatusBadge
                        onClick={() => goToFacilitator(f)}
                      />
                    );
                  })}
            </SimpleGrid>
            <ListPagination
              layout="grid"
              page={activePage}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={(page) => setActivePage(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setActivePage(1);
              }}
              showPageSize
            />
          </Card>
        )
      }
    </Container>
  );
}